// src/components/features/Structure/components/StructureStep1Location.jsx
// Step 1: City/State/District search using OpenStreetMap Nominatim (free, no API key)
// + Local seismic zone database (IS 1893:2016)

import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  VStack,
  Radio,
  Spinner,
} from "@chakra-ui/react";
import { useState, useRef, useCallback } from "react";

// ─── Seismic Zone Database (IS 1893:2016) ────────────────────────────────────
// Zone V = Very High | IV = High | III = Moderate | II = Low
const ZONE_DB = {
  // ── Zone V ──
  "guwahati": "V", "dispur": "V", "shillong": "V", "itanagar": "V",
  "naharlagun": "V", "imphal": "V", "thoubal": "V", "bishnupur": "V",
  "kohima": "V", "dimapur": "V", "aizawl": "V", "lunglei": "V",
  "agartala": "V", "gangtok": "V", "namchi": "V", "srinagar": "V",
  "baramulla": "V", "anantnag": "V", "kupwara": "V", "pulwama": "V",
  "bandipora": "V", "kulgam": "V", "shopian": "V", "leh": "V",
  "kargil": "V", "dibrugarh": "V", "jorhat": "V", "tinsukia": "V",
  "sibsagar": "V", "golaghat": "V", "sivasagar": "V", "dhemaji": "V",
  "lakhimpur": "V", "majuli": "V", "bongaigaon": "V", "barpeta": "V",
  "nalbari": "V", "kamrup": "V", "goalpara": "V", "dhubri": "V",
  "chirang": "V", "kokrajhar": "V", "port blair": "V", "car nicobar": "V",
  "kohima district": "V", "wokha": "V", "mokokchung": "V", "tuensang": "V",
  "mon": "V", "phek": "V", "zunheboto": "V", "longleng": "V",
  "peren": "V", "kiphire": "V",

  // ── Zone IV ──
  "delhi": "IV", "new delhi": "IV", "north delhi": "IV", "south delhi": "IV",
  "east delhi": "IV", "west delhi": "IV", "central delhi": "IV",
  "north east delhi": "IV", "north west delhi": "IV", "south west delhi": "IV",
  "shahdara": "IV", "jammu": "IV", "kathua": "IV", "udhampur": "IV",
  "reasi": "IV", "ramban": "IV", "doda": "IV", "kishtwar": "IV",
  "rajouri": "IV", "poonch": "IV", "shimla": "IV", "kullu": "IV",
  "mandi": "IV", "dharamsala": "IV", "dharamshala": "IV", "kangra": "IV",
  // NOTE: "hamirpur" and "bilaspur" below are the Himachal Pradesh
  // districts. UP also has a Hamirpur (Zone III) and Chhattisgarh also
  // has a Bilaspur (Zone III) — this flat name-only lookup can't tell
  // them apart, so the higher/more conservative zone (IV) is kept as
  // the default rather than silently under-reporting seismic risk.
  "chamba": "IV", "una": "IV", "hamirpur": "IV", "bilaspur": "IV",
  "solan": "IV", "sirmaur": "IV", "kinnaur": "IV", "lahaul and spiti": "IV",
  "dehradun": "IV", "haridwar": "IV", "rishikesh": "IV", "roorkee": "IV",
  "tehri": "IV", "uttarkashi": "IV", "chamoli": "IV", "rudraprayag": "IV",
  "pithoragarh": "IV", "bageshwar": "IV", "almora": "IV", "champawat": "IV",
  "nainital": "IV", "udham singh nagar": "IV", "pauri garhwal": "IV",
  "chandigarh": "IV", "ambala": "IV", "karnal": "IV", "panipat": "IV",
  "sonipat": "IV", "rohtak": "IV", "jhajjar": "IV", "ludhiana": "IV",
  "amritsar": "IV", "jalandhar": "IV", "patiala": "IV", "mohali": "IV",
  "gurdaspur": "IV", "hoshiarpur": "IV", "nawanshahr": "IV", "pathankot": "IV",
  "firozpur": "IV", "ferozepur": "IV", "fazilka": "IV", "moga": "IV",
  "kapurthala": "IV", "rupnagar": "IV", "ropar": "IV", "fatehgarh sahib": "IV",
  "sahibzada ajit singh nagar": "IV", "tarn taran": "IV", "muktsar": "IV",
  "bathinda": "IV", "mansa": "IV", "sangrur": "IV", "barnala": "IV",
  "faridkot": "IV", "meerut": "IV", "moradabad": "IV",
  "bareilly": "IV", "aligarh": "IV", "rampur": "IV", "saharanpur": "IV",
  "muzaffarnagar": "IV", "shamli": "IV", "bijnor": "IV", "amroha": "IV",
  "hapur": "IV", "bulandshahr": "IV", "gautam buddha nagar": "IV",
  "noida": "IV", "ghaziabad": "IV", "gorakhpur": "IV", "maharajganj": "IV",
  "deoria": "IV", "kushinagar": "IV", "lucknow": "IV", "sitapur": "IV",
  "lakhimpur kheri": "IV", "bahraich": "IV", "shravasti": "IV",
  "balrampur": "IV", "gonda": "IV", "basti": "IV", "santkabir nagar": "IV",
  "siddharthnagar": "IV", "patna": "IV", "bhagalpur": "IV", "gaya": "IV",
  "muzaffarpur": "IV", "darbhanga": "IV", "sitamarhi": "IV",
  "sheohar": "IV", "madhubani": "IV", "supaul": "IV", "araria": "IV",
  "kishanganj": "IV", "purnia": "IV", "katihar": "IV", "siliguri": "IV",
  "darjeeling": "IV", "kalimpong": "IV", "kurseong": "IV",
  "agra": "IV", "mathura": "IV", "hathras": "IV", "firozabad": "IV",
  "etah": "IV", "mainpuri": "IV", "kasganj": "IV",

  // ── Zone III ──
  "mumbai": "III", "thane": "III", "navi mumbai": "III", "kalyan": "III",
  "dombivli": "III", "pune": "III", "pimpri chinchwad": "III",
  "nagpur": "III", "nashik": "III", "aurangabad": "III", "sambhajinagar": "III",
  "solapur": "III", "amravati": "III", "kolhapur": "III", "satara": "III",
  "sangli": "III", "ratnagiri": "III", "sindhudurg": "III", "nanded": "III",
  "latur": "III", "osmanabad": "III", "dharashiv": "III", "jalna": "III",
  "beed": "III", "hingoli": "III", "parbhani": "III", "washim": "III",
  "yavatmal": "III", "wardha": "III", "chandrapur": "III", "gadchiroli": "III",
  "gondia": "III", "bhandara": "III", "raigad": "III", "palgar": "III",
  "dhule": "III", "nandurbar": "III", "jalgaon": "III", "ahmednagar": "III",
  "kolkata": "III", "howrah": "III", "hooghly": "III", "north 24 parganas": "III",
  "south 24 parganas": "III", "nadia": "III", "murshidabad": "III",
  "birbhum": "III", "bardhaman": "III", "burdwan": "III", "purba bardhaman": "III",
  "paschim bardhaman": "III", "bankura": "III", "purulia": "III",
  "jhargram": "III", "paschim medinipur": "III", "purba medinipur": "III",
  "ahmedabad": "III", "surat": "III", "vadodara": "III", "rajkot": "III",
  "bhavnagar": "III", "gandhinagar": "III", "jamnagar": "III",
  "junagadh": "III", "porbandar": "III", "amreli": "III", "anand": "III",
  "kheda": "III", "mehsana": "III", "patan": "III", "banaskantha": "III",
  "sabarkantha": "III", "aravalli": "III", "gandhinagar district": "III",
  "surendranagar": "III", "morbi": "III", "botad": "III", "gir somnath": "III",
  "devbhoomi dwarka": "III", "kutch": "III", "kachchh": "III",
  "bhopal": "III", "indore": "III", "gwalior": "III", "jabalpur": "III",
  "ujjain": "III", "sagar": "III", "rewa": "III", "satna": "III",
  "dewas": "III", "ratlam": "III", "mandsaur": "III", "neemuch": "III",
  "shajapur": "III", "agar malwa": "III", "rajgarh": "III", "vidisha": "III",
  "raisen": "III", "sehore": "III", "hoshangabad": "III", "narmadapuram": "III",
  "harda": "III", "betul": "III", "chhindwara": "III", "seoni": "III",
  "mandla": "III", "dindori": "III", "balaghat": "III", "katni": "III",
  "shahdol": "III", "anuppur": "III", "umaria": "III", "panna": "III",
  "chhatarpur": "III", "tikamgarh": "III", "datia": "III", "shivpuri": "III",
  "guna": "III", "ashoknagar": "III", "morena": "III", "bhind": "III",
  "sheopur": "III", "sidhi": "III", "singrauli": "III", "narsinghpur": "III",
  "raipur": "III", "durg": "III", "bhilai": "III",
  "rajnandgaon": "III", "korba": "III", "raigarh": "III", "janjgir champa": "III",
  "jashpur": "III", "sarguja": "III", "balrampur chhattisgarh": "III",
  "surajpur": "III", "koriya": "III", "surguja": "III", "balod": "III",
  "bemetara": "III", "baloda bazar": "III", "gariaband": "III",
  "mahasamund": "III", "dhamtari": "III", "kanker": "III", "kondagaon": "III",
  "bastar": "III", "dantewada": "III", "sukma": "III", "bijapur": "III",
  "narayanpur": "III", "kabirdham": "III", "kawardha": "III", "mungeli": "III",
  "jaipur": "III", "jodhpur": "III", "kota": "III", "ajmer": "III",
  "bikaner": "III", "udaipur": "III", "alwar": "III", "bharatpur": "III",
  "sikar": "III", "jhunjhunu": "III", "churu": "III", "ganganagar": "III",
  "sri ganganagar": "III", "hanumangarh": "III", "nagaur": "III",
  "pali": "III", "jalore": "III", "barmer": "III", "jaisalmer": "III",
  "chittorgarh": "III", "rajsamand": "III", "bhilwara": "III",
  "dungarpur": "III", "banswara": "III", "pratapgarh": "III", "sirohi": "III",
  "bundi": "III", "baran": "III", "jhalawar": "III", "karauli": "III",
  "sawai madhopur": "III", "tonk": "III", "dausa": "III", "dholpur": "III",
  "hyderabad": "III", "rangareddy": "III", "medchal": "III",
  "sangareddy": "III", "siddipet": "III", "nizamabad": "III",
  "karimnagar": "III", "warangal": "III", "hanamkonda": "III",
  "khammam": "III", "nalgonda": "III", "suryapet": "III", "yadadri": "III",
  "mahabubnagar": "III", "nagarkurnool": "III", "wanaparthy": "III",
  "jogulamba gadwal": "III", "vikarabad": "III", "bhadradri kothagudem": "III",
  "jayashankar bhupalpally": "III", "mulugu": "III", "narayanpet": "III",
  "mancherial": "III", "peddapalli": "III", "kamareddy": "III",
  "rajanna sircilla": "III", "jagtial": "III", "nirmal": "III",
  "nizamabad district": "III", "mahabubabad": "III", "adilabad": "III",
  "kumuram bheem": "III", "asifabad": "III", "visakhapatnam": "III",
  "vijayawada": "III", "guntur": "III", "krishna": "III", "west godavari": "III",
  "east godavari": "III", "alluri sitharama raju": "III", "anakapalli": "III",
  "kakinada": "III", "konaseema": "III", "eluru": "III",
  "bangalore": "III", "bengaluru": "III", "bengaluru urban": "III",
  "bengaluru rural": "III", "mysuru": "III", "mysore": "III",
  "tumkur": "III", "tumakuru": "III", "kolar": "III", "chikkaballapur": "III",
  "ramanagara": "III", "mandya": "III", "hassan": "III", "kodagu": "III",
  "chikkamagaluru": "III", "shivamogga": "III", "davangere": "III",
  "chitradurga": "III", "ballari": "III", "bellary": "III",
  "vijayanagara": "III", "koppal": "III", "raichur": "III", "yadgir": "III",
  "kalaburagi": "III", "gulbarga": "III", "vijayapura": "III",
  "bijapur karnataka": "III", "bagalkot": "III", "gadag": "III",
  "dharwad": "III", "hubli": "III", "haveri": "III", "belagavi": "III",
  "belgaum": "III", "uttara kannada": "III", "udupi": "III",
  "dakshina kannada": "III", "mangalore": "III", "mangaluru": "III",
  "bhubaneswar": "III", "cuttack": "III", "puri": "III",
  "khordha": "III", "khurda": "III", "jagatsinghpur": "III",
  "kendrapara": "III", "jajpur": "III", "bhadrak": "III", "balasore": "III",
  "mayurbhanj": "III", "keonjhar": "III", "kendujhar": "III",
  "sundargarh": "III", "sambalpur": "III", "jharsuguda": "III",
  "bargarh": "III", "bolangir": "III", "sonepur": "III", "boudh": "III",
  "deogarh": "III", "angul": "III", "dhenkanal": "III", "nayagarh": "III",
  "ganjam": "III", "gajapati": "III", "rayagada": "III", "koraput": "III",
  "malkangiri": "III", "nabarangpur": "III", "kalahandi": "III",
  "nuapada": "III", "ranchi": "III", "jamshedpur": "III", "dhanbad": "III",
  "bokaro": "III", "hazaribagh": "III", "giridih": "III", "koderma": "III",
  "ramgarh": "III", "lohardaga": "III", "gumla": "III", "simdega": "III",
  "khunti": "III", "west singhbhum": "III", "east singhbhum": "III",
  "saraikela kharsawan": "III", "chatra": "III", "latehar": "III",
  "palamu": "III", "garhwa": "III", "dumka": "III", "jamtara": "III",
  "deoghar": "III", "godda": "III", "sahibganj": "III", "pakur": "III",
  "varanasi": "III", "allahabad": "III", "prayagraj": "III", "kanpur": "III",
  "kanpur nagar": "III", "kanpur dehat": "III", "fatehpur": "III",
  "kaushambi": "III", "chitrakoot": "III", "banda": "III",
  "mahoba": "III", "jhansi": "III", "lalitpur": "III", "jalaun": "III",
  "orai": "III", "etawah": "III", "auraiya": "III", "farrukhabad": "III",
  "kannauj": "III", "hardoi": "III", "unnao": "III", "rae bareli": "III",
  "amethi": "III", "sultanpur": "III", "faizabad": "III", "ambedkar nagar": "III",
  "ayodhya": "III", "pratapgarh up": "III", "jaunpur": "III",
  "ghazipur": "III", "ballia": "III", "bhadohi": "III", "mirzapur": "III",
  "sonbhadra": "III", "chandauli": "III", "sant ravidas nagar": "III",
  "azamgarh": "III", "mau": "III",
  "gurgaon": "III", "gurugram": "III", "faridabad": "III",
  "bahadurgarh": "III", "palwal": "III", "nuh": "III", "rewari": "III",
  "mahendragarh": "III", "bhiwani": "III", "hisar": "III", "sirsa": "III",
  "fatehabad": "III", "jind": "III", "kaithal": "III", "kurukshetra": "III",
  "yamunanagar": "III", "panchkula": "III",

  // ── Zone II ──
  "chennai": "II", "coimbatore": "II", "madurai": "II", "trichy": "II",
  "tiruchirappalli": "II", "tirunelveli": "II", "salem": "II", "vellore": "II",
  "ranipet": "II", "tirupathur": "II", "tiruvannamalai": "II",
  "villupuram": "II", "cuddalore": "II", "nagapattinam": "II",
  "thanjavur": "II", "tiruvarur": "II", "ariyalur": "II", "perambalur": "II",
  "pudukottai": "II", "sivaganga": "II", "virudhunagar": "II",
  "ramanathapuram": "II", "thoothukudi": "II", "tuticorin": "II",
  "kanyakumari": "II", "tiruppur": "II", "erode": "II", "the nilgiris": "II",
  "ooty": "II", "dindigul": "II", "namakkal": "II", "karur": "II",
  "dharmapuri": "II", "krishnagiri": "II", "kallakurichi": "II",
  "tirupattur": "II", "chengalpattu": "II", "kancheepuram": "II",
  "tiruvallur": "II", "tirupati": "II",
  "kochi": "II", "cochin": "II", "ernakulam": "II",
  "thiruvananthapuram": "II", "trivandrum": "II",
  "kozhikode": "II", "calicut": "II", "thrissur": "II", "trichur": "II",
  "kannur": "II", "kasaragod": "II", "malappuram": "II", "palakkad": "II",
  "wayanad": "II", "idukki": "II", "pathanamthitta": "II", "alappuzha": "II",
  "kollam": "II", "kottayam": "II",
  "pondicherry": "II", "puducherry": "II", "karaikal": "II",
  "nellore": "II", "sri potti sriramulu nellore": "II",
  "srikakulam": "II", "vizianagaram": "II", "parvathipuram manyam": "II",
  "anantapur": "II", "sri sathya sai": "II", "kurnool": "II",
  "nandyal": "II", "kadapa": "II", "ysr kadapa": "II",
  "prakasam": "II", "chittoor": "II",
};

// ─── Seismic Zone Info ────────────────────────────────────────────────────────
const ZONE_INFO = {
  V: { label: "Zone V — Very High Seismic Risk", color: "red.600", bg: "red.50", border: "#FC8181" },
  IV: { label: "Zone IV — High Seismic Risk", color: "orange.600", bg: "orange.50", border: "#F6AD55" },
  III: { label: "Zone III — Moderate Seismic Risk", color: "yellow.700", bg: "yellow.50", border: "#ECC94B" },
  II: { label: "Zone II — Low Seismic Risk", color: "green.600", bg: "green.50", border: "#68D391" },
};

function getZone(name) {
  return ZONE_DB[name.trim().toLowerCase()] || null;
}

// Fuzzy search from local DB
function searchLocal(query) {
  const q = query.trim().toLowerCase();
  if (!q || q.length < 2) return [];
  return Object.keys(ZONE_DB)
    .filter((k) => k.includes(q))
    .sort((a, b) => {
      const aStart = a.startsWith(q) ? 0 : 1;
      const bStart = b.startsWith(q) ? 0 : 1;
      return aStart - bStart || a.localeCompare(b);
    })
    .slice(0, 8)
    .map((k) => ({
      name: k.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
      raw: k,
    }));
}

export default function StructureStep1Location({
  formData, setFormData, onNext, onBack, isLastStep, onSubmit, viewMode,
}) {
  const [inputVal, setInputVal] = useState(formData?.structureCity || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zone, setZone] = useState(
    formData?.seismicZone
      ? { code: formData.seismicZone, city: formData.structureCity }
      : null
  );
  const debounceTimer = useRef(null);

  // Nominatim search (OpenStreetMap, free, no key)
  const searchNominatim = useCallback(async (query) => {
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query + " India")}&format=json&addressdetails=1&limit=8&countrycodes=in&accept-language=en`;
      const res = await fetch(url, {
        headers: { "Accept-Language": "en", "User-Agent": "StructureFormApp/1.0" },
      });
      const data = await res.json();
      return data.map((item) => {
        const addr = item.address || {};
        const name =
          addr.city || addr.town || addr.village || addr.county ||
          addr.state_district || addr.suburb || item.display_name.split(",")[0];
        const state = addr.state || "";
        return {
          name: name.trim(),
          raw: name.trim().toLowerCase(),
          display: state ? `${name.trim()}, ${state}` : item.display_name.split(",").slice(0, 2).join(",").trim(),
        };
      }).filter((v, i, arr) => arr.findIndex((x) => x.raw === v.raw) === i); // dedupe
    } catch {
      return [];
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputVal(val);
    setZone(null);
    setSuggestions([]);

    if (!val.trim() || val.length < 2) return;

    // Local search first (instant)
    const local = searchLocal(val);
    if (local.length > 0) {
      setSuggestions(local.map((l) => ({ ...l, display: l.name + ", India" })));
    }

    // Nominatim search (debounced)
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      const remote = await searchNominatim(val);
      setLoading(false);
      if (remote.length > 0) {
        // Merge: local first, then remote extras
        const allRaws = new Set(local.map((l) => l.raw));
        const extras = remote.filter((r) => !allRaws.has(r.raw));
        setSuggestions([...local.map((l) => ({ ...l, display: l.name + ", India" })), ...extras].slice(0, 8));
      }
    }, 350);
  };

  const handleSelect = (suggestion) => {
    const name = suggestion.name;
    setInputVal(name);
    setSuggestions([]);
    clearTimeout(debounceTimer.current);
    setLoading(false);

    const detectedZone = getZone(suggestion.raw || name);
    const zoneCode = detectedZone || "III"; // default moderate if not in DB
    setZone({ code: zoneCode, city: name, unknown: !detectedZone });
    setFormData({
      ...formData,
      structureCity: name,
      seismicZone: zoneCode,
      seismicRisk: ZONE_INFO[zoneCode].label,
    });
  };

  const handleNext = () => {
    if (!viewMode && !zone) return;
    if (isLastStep) onSubmit();
    else onNext();
  };

  const zoneInfo = zone ? ZONE_INFO[zone.code] : null;

  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      <Box
        flex="1"
        minH="0"
        overflowY="auto"
        overflowX="hidden"
        pr={2}
        css={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" },
          "& *": { pointerEvents: viewMode ? "none" : "auto" },
        }}
      >
        <VStack spacing={4} align="stretch">
          <Box textAlign="center">
            <Text fontSize="17px" fontWeight="600" color="gray.800">
              Project Location
            </Text>
            <Text fontSize="12px" color="gray.500" mt={1}>
              Search city, district or state to detect Seismic Zone
            </Text>
            {!zone && !loading && inputVal.trim().length >= 2 && suggestions.length === 0 && (
              <Text fontSize="12px" color="red" mt={0.5}>
                Can't find your village? Try selecting its district instead
              </Text>
            )}
          </Box>

          {/* Search Input */}
          <Box position="relative">
            <Flex align="center" position="relative">
              <Input
                value={inputVal}
                onChange={handleInputChange}
                placeholder="Type city, district or state..."
                h="42px"
                fontSize="14px"
                borderRadius="8px"
                borderColor="gray.300"
                pr={loading ? "36px" : "12px"}
                _focus={{ borderColor: "cyan.500", boxShadow: "0 0 0 1px #00b5d8" }}
                autoComplete="off"
              />
              {loading && (
                <Spinner
                  size="sm"
                  color="cyan.500"
                  position="absolute"
                  right="10px"
                />
              )}
            </Flex>

            {/* Dropdown */}
            {suggestions.length > 0 && (
              <Box
                position="absolute"
                top="44px"
                left="0"
                right="0"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="8px"
                boxShadow="lg"
                zIndex={20}
                overflow="hidden"
              >
                {suggestions.map((s, i) => (
                  <Box
                    key={i}
                    px={3}
                    py={2}
                    cursor="pointer"
                    bg="white"
                    _hover={{ bg: "cyan.50" }}
                    borderBottom={i < suggestions.length - 1 ? "1px solid" : "none"}
                    borderColor="gray.100"
                    onClick={() => handleSelect(s)}
                  >
                    <Text fontSize="13px" fontWeight="600" color="gray.800">
                      {s.name}
                    </Text>
                    <Text fontSize="11px" color="gray.400">
                      {s.display}
                    </Text>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Zone Result */}
          {zone && zoneInfo && (
            <Box
              border="2px solid"
              borderColor={zoneInfo.border}
              borderRadius="10px"
              bg={zoneInfo.bg}
              px={4}
              py={3}
            >
              <Flex align="center" gap={3}>
                <Radio isChecked colorScheme="cyan" readOnly onChange={() => { }} />
                <Box>
                  <Text fontSize="14px" fontWeight="700" color={zoneInfo.color}>
                    {zoneInfo.label}
                  </Text>
                  <Text fontSize="11px" color="gray.500" mt={0.5}>
                    {zone.unknown
                      ? `${zone.city} — Zone III assigned (exact zone not in database)`
                      : `${zone.city} is in Seismic ${zoneInfo.label}`}
                  </Text>
                </Box>
              </Flex>
            </Box>
          )}

          {/* Info */}
          {!zone && (
            <Box bg="gray.50" borderRadius="8px" px={3} py={2}>
              <Text fontSize="11px" color="gray.400" textAlign="center">
                Seismic zone is used to determine earthquake-resistant design as per IS 1893:2016
              </Text>
            </Box>
          )}
        </VStack>
      </Box>

      {/* Navigation */}
      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button
          onClick={onBack}
          variant="solid" bg="white" color="cyan.600"
          border="1px solid" borderColor="cyan.500"
          borderRadius="6px" minW="100px" h="40px"
          fontWeight="500" fontSize="14px"
          _hover={{ bg: "cyan.50" }}
        >
          ← Previous
        </Button>
        <Button
          onClick={handleNext}
          isDisabled={viewMode ? false : !zone}
          variant="solid"
          bg={(viewMode || zone) ? "cyan.500" : "gray.300"}
          color="white" borderRadius="6px" minW="100px" h="40px"
          fontWeight="500" fontSize="14px" ml="auto"
          _hover={{ bg: (viewMode || zone) ? "cyan.600" : "gray.300" }}
          _disabled={{ bg: "gray.300", color: "gray.500", cursor: "not-allowed" }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}